import { useCallback, useState, useSyncExternalStore } from 'react';

import useAlert from 'src/hooks/useAlert';

import {
  postMocaPrompt,
  postMocaPromptReason,
  undoMocaPrompt,
} from '../../services/careOrchestrationApi';

const EMPTY_STATE = {
  team: null,
  provider: null,
  log: [],
  outcome: 'undecided',
  outcomeNote: null,
  final: false,
  changedByProvider: false,
  decidedByProvider: false,
  hiddenInPrepare: false,
  afterVisit: null,
};
const states = new Map();
const listeners = new Set();

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const publish = (appointmentId, state) => {
  states.set(appointmentId, state);
  listeners.forEach((listener) => listener());
};

const useMocaPrompt = (appt, place) => {
  const { showAlert, AlertType } = useAlert();
  const [pending, setPending] = useState(false);
  const appointmentId = appt?.id;
  const stored = useSyncExternalStore(subscribe, () => states.get(appointmentId));
  const state = stored ?? appt?.mocaPrompt ?? EMPTY_STATE;

  const run = useCallback(
    async (request) => {
      setPending(true);
      try {
        const next = await request();
        publish(appointmentId, { ...next, afterVisit: state.afterVisit });
      } catch (error) {
        showAlert(AlertType.Error, '', error?.message || 'Failed to save');
      } finally {
        setPending(false);
      }
    },
    [appointmentId, showAlert, AlertType, state.afterVisit],
  );

  const decide = useCallback(
    (kind, choice, note, instead) =>
      run(() => postMocaPrompt(appointmentId, { kind, choice, note, instead, place })),
    [run, appointmentId, place],
  );

  const undo = useCallback(
    (kind) => run(() => undoMocaPrompt(appointmentId, { kind, place })),
    [run, appointmentId, place],
  );

  const addReason = useCallback(
    (reason) => run(() => postMocaPromptReason(appointmentId, { reason, place })),
    [run, appointmentId, place],
  );

  return { state, pending, decide, undo, addReason };
};

export default useMocaPrompt;
